// src/pages/Orders/OrderForm/OrderForm.jsx - COMPLETE THREE-MODE IMPLEMENTATION
import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useOrderForm } from "./orderFormHooks";
import {
  FiArrowLeft,
  FiSave,
  FiCheck,
  FiAlertCircle,
  FiShoppingCart,
  FiUsers,
  FiRefreshCw,
  FiTruck,
  FiCreditCard,
  FiFileText,
  FiInfo,
  FiX,
  FiCheckCircle,
  FiClock,
  FiDollarSign,
  FiPercent,
} from "react-icons/fi";
import Button from "../../../components/common/Button";
import Badge from "../../../components/common/Badge";
import {
  CustomerSelectionSection,
  ItemsSection,
  FulfillmentSection,
  PaymentSection,
  NotesSection,
  SessionSelectionSection,
  EnhancedExchangeSection,
  EnhancedPricingSummarySection,
} from "./OrderFormSections";
import OrderSourceIndicator from "./OrderFormSections/OrderSourceIndicator";
import ExistingAdjustmentsDisplay from "./OrderFormSections/ExistingAdjustmentsDisplay";
import OrderAdjustmentsSection from "./OrderFormSections/OrderAdjustmentsSection";
import UserSelection from "../../User/components/UserSelection";
import ProductSelection from "../../../components/products/ProductSelection";
import { notifySuccess, notifyError } from "../../../utils/notification";

const OrderForm = () => {
  const navigate = useNavigate();
  const { orderId } = useParams();
  const mode = orderId ? "edit" : "add";

  // Use custom hook with three-mode support
  const {
    // Form state
    formData,
    errors,
    selectedUser,
    selectedSession,
    calculating,
    pricingSummary,
    sessionPricing,

    // Source info
    orderSource,
    isFromCart,
    isFromSession,
    cartData,
    sessionData,
    cartAdjustments,
    sessionAdjustments,

    // UI state
    showUserSelection,
    setShowUserSelection,
    showProductSelection,
    setShowProductSelection,
    loading,

    // Form handlers
    handleChange,
    handleSelectUser,
    handleDeselectUser,
    handleSelectSession,
    handleDeselectSession,
    handleAddProducts,
    handleUpdateQuantity,
    handleRemoveItem,
    handleAddExchangeItem,
    handleUpdateExchangeItem,
    handleRemoveExchangeItem,
    handleAddOrderAdjustment,
    handleUpdateOrderAdjustment,
    handleRemoveOrderAdjustment,
    handleRecalculatePricing,
    handleSubmit,
    handleSaveDraft,

    // Data
    branches,
    adminInfo,
  } = useOrderForm(mode, orderId);

  // Local state
  const [submitting, setSubmitting] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);

  // Section completion tracking
  const [sectionCompletion, setSectionCompletion] = useState({
    customer: false,
    session: false,
    items: false,
    exchange: false,
    fulfillment: false,
    payment: false,
  });

  // Update section completion
  useEffect(() => {
    setSectionCompletion({
      customer: Boolean(formData.userId || formData.guestCustomer?.name),
      session: Boolean(selectedSession),
      items: formData.items.length > 0,
      exchange: true, // Optional
      fulfillment: Boolean(formData.fulfillmentLocation?.branchCode),
      payment: Boolean(formData.payment?.method),
    });
  }, [formData, selectedSession]);

  // Handle form submission
  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    setSubmitting(true);
    try {
      const result = await handleSubmit();
      if (result) {
        notifySuccess(
          mode === "edit"
            ? "Order updated successfully"
            : "Order created successfully"
        );
        navigate(`/orders/${result._id || result.orderId}`);
      }
    } catch (error) {
      console.error("Form submission error:", error);
      notifyError(error.message || "Failed to create order");

      // Scroll to first error
      const firstError = Object.keys(errors)[0];
      if (firstError) {
        const element = document.querySelector(`[name="${firstError}"]`);
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Handle save draft
  const onSaveDraft = async () => {
    if (savingDraft) return;
    setSavingDraft(true);
    try {
      const result = await handleSaveDraft();
      notifySuccess("Draft saved successfully");
      if (mode === "add" && result) {
        navigate(`/orders/${result._id}/edit`);
      }
    } catch (error) {
      console.error("Save draft error:", error);
      notifyError(error.message || "Failed to save draft");
    } finally {
      setSavingDraft(false);
    }
  };

  // Handle back navigation
  const handleBack = () => {
    if (formData.items.length > 0) {
      if (
        window.confirm(
          "You have unsaved changes. Are you sure you want to leave?"
        )
      ) {
        navigate("/orders");
      }
    } else {
      navigate("/orders");
    }
  };

  // Calculate overall progress
  const completedSections =
    Object.values(sectionCompletion).filter(Boolean).length;
  const totalSections = 5; // customer, items, fulfillment, payment (exchange/session optional)
  const progressPercentage = (completedSections / totalSections) * 100;

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  const formatDate = (date) => {
    if (!date) return "-";
    return new Date(date).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-32">
      {/* Main Content */}
      <div className="container mx-auto px-6 py-6">
        <form onSubmit={onSubmit}>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column - Main Sections */}
            <div className="lg:col-span-2 space-y-6">
              {/* Order Source Indicator */}
              <OrderSourceIndicator
                source={orderSource}
                sourceData={
                  isFromSession ? sessionData : isFromCart ? cartData : null
                }
                formatCurrency={formatCurrency}
                formatDate={formatDate}
              />

              {/* Error Summary */}
              {Object.keys(errors).length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <FiAlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <h3 className="font-semibold text-red-900 mb-2">
                        Please fix the following errors:
                      </h3>
                      <ul className="space-y-1 text-sm text-red-700">
                        {Object.entries(errors).map(([field, error]) => (
                          <li key={field} className="flex items-start gap-2">
                            <span className="text-red-500">•</span>
                            <span>
                              {field.replace(/\./g, " ")}: {error}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              {/* Section 1: Customer Selection */}
              <SectionWrapper
                id="customer"
                title="Customer Details"
                icon={FiUsers}
                completed={sectionCompletion.customer}
              >
                <CustomerSelectionSection
                  formData={formData}
                  selectedUser={selectedUser}
                  onSelectUser={() => setShowUserSelection(true)}
                  onDeselectUser={handleDeselectUser}
                  onChange={handleChange}
                  errors={errors}
                />
              </SectionWrapper>

              {/* Section 2: Session Selection - Only if NOT from session/cart */}
              {!isFromSession && !isFromCart && (
                <SectionWrapper
                  id="session"
                  title="Link Consultation Session"
                  icon={FiRefreshCw}
                  completed={Boolean(selectedSession)}
                  optional
                >
                  <SessionSelectionSection
                    selectedSession={selectedSession}
                    onSelectSession={handleSelectSession}
                    onDeselectSession={handleDeselectSession}
                  />
                </SectionWrapper>
              )}

              {/* Section 3: Order Items */}
              <SectionWrapper
                id="items"
                title="Order Items"
                icon={FiShoppingCart}
                completed={sectionCompletion.items}
              >
                <ItemsSection
                  items={formData.items}
                  errors={errors}
                  onAddItem={handleAddProducts}
                  onUpdateQuantity={handleUpdateQuantity}
                  onRemoveItem={handleRemoveItem}
                  onShowProductSelection={() => setShowProductSelection(true)}
                />
              </SectionWrapper>

              {/* Section 4: Existing Adjustments (Cart/Session) - Read-only */}
              {(cartAdjustments.length > 0 ||
                sessionAdjustments.length > 0) && (
                <ExistingAdjustmentsDisplay
                  source={orderSource}
                  cartAdjustments={cartAdjustments}
                  sessionAdjustments={sessionAdjustments}
                  formatCurrency={formatCurrency}
                />
              )}

              {/* Section 5: Order-Level Adjustments - Editable */}
              <SectionWrapper
                id="order-adjustments"
                title="Order-Level Adjustments"
                icon={FiPercent}
                completed={formData.orderAdjustments?.length > 0}
                optional
              >
                <OrderAdjustmentsSection
                  orderAdjustments={formData.orderAdjustments || []}
                  items={formData.items}
                  onAddAdjustment={handleAddOrderAdjustment}
                  onUpdateAdjustment={handleUpdateOrderAdjustment}
                  onRemoveAdjustment={handleRemoveOrderAdjustment}
                  formatCurrency={formatCurrency}
                />
              </SectionWrapper>

              {/* Section 6: Exchange Items */}
              <SectionWrapper
                id="exchange"
                title="Exchange Items"
                icon={FiRefreshCw}
                completed={sectionCompletion.exchange}
                optional
              >
                <EnhancedExchangeSection
                  exchangeItems={formData.exchangeItems}
                  onAddItem={handleAddExchangeItem}
                  onUpdateItem={handleUpdateExchangeItem}
                  onRemoveItem={handleRemoveExchangeItem}
                  errors={errors}
                  adminInfo={adminInfo} // ADD THIS
                />
              </SectionWrapper>

              {/* Section 7: Fulfillment */}
              <SectionWrapper
                id="fulfillment"
                title="Fulfillment Details"
                icon={FiTruck}
                completed={sectionCompletion.fulfillment}
              >
                <FulfillmentSection
                  formData={formData}
                  onChange={handleChange}
                  errors={errors}
                  branches={branches}
                />
              </SectionWrapper>

              {/* Section 8: Payment */}
              <SectionWrapper
                id="payment"
                title="Payment Details"
                icon={FiCreditCard}
                completed={sectionCompletion.payment}
              >
                <PaymentSection
                  formData={formData}
                  onChange={handleChange}
                  errors={errors}
                />
              </SectionWrapper>

              {/* Section 9: Notes */}
              <SectionWrapper
                id="notes"
                title="Order Notes"
                icon={FiFileText}
                completed={Boolean(formData.notes || formData.adminNotes)}
                optional
              >
                <NotesSection formData={formData} onChange={handleChange} />
              </SectionWrapper>
            </div>

            {/* Right Column - Pricing Summary (Sticky) */}
            <div className="lg:col-span-1">
              <div className="sticky top-24">
                <EnhancedPricingSummarySection
                  pricingSummary={pricingSummary}
                  calculating={calculating}
                  onRecalculate={handleRecalculatePricing}
                  sessionPricing={sessionPricing}
                  priceAdjustments={[
                    ...(cartAdjustments || []),
                    ...(sessionAdjustments || []),
                    ...(formData.orderAdjustments || []),
                  ]}
                  priceLock={selectedSession?.priceLock}
                />

                {/* Session Pricing Lock Info */}
                {selectedSession?.priceLock?.isLocked && (
                  <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <div className="flex items-start gap-2">
                      <FiAlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-semibold text-yellow-900 mb-1">
                          Prices Locked from Session
                        </p>
                        <p className="text-xs text-yellow-700">
                          Locked by {selectedSession.priceLock.lockedBy}
                        </p>
                        <p className="text-xs text-yellow-600 mt-1">
                          <FiClock className="inline w-3 h-3 mr-1" />
                          Expires:{" "}
                          {formatDate(selectedSession.priceLock.lockExpiresAt)}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </form>
      </div>

      {/* Fixed Bottom Action Bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-40">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Pricing Summary (Left) */}
            <div className="flex items-center gap-6">
              {pricingSummary && (
                <>
                  <div>
                    <p className="text-sm text-gray-600">Subtotal</p>
                    <p className="text-xl font-bold text-gray-900">
                      {formatCurrency(pricingSummary.subtotal)}
                    </p>
                  </div>
                  {pricingSummary.totalExchangeValue > 0 && (
                    <div>
                      <p className="text-sm text-gray-600">Exchange Credit</p>
                      <p className="text-xl font-bold text-green-600">
                        -{formatCurrency(pricingSummary.totalExchangeValue)}
                      </p>
                    </div>
                  )}
                  <div>
                    <p className="text-sm text-gray-600">
                      {pricingSummary.totalExchangeValue > 0
                        ? "Net Payable"
                        : "Grand Total"}
                    </p>
                    <p className="text-2xl font-bold text-primary">
                      {formatCurrency(
                        pricingSummary.netPayable || pricingSummary.amountDue
                      )}
                    </p>
                  </div>
                </>
              )}
            </div>

            {/* Actions (Right) */}
            <div className="flex items-center gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={handleBack}
                disabled={submitting || savingDraft}
              >
                <FiX className="w-4 h-4 mr-2" />
                Cancel
              </Button>

              {mode === "add" && formData.items.length > 0 && (
                <Button
                  type="button"
                  variant="secondary"
                  onClick={onSaveDraft}
                  disabled={savingDraft || submitting || loading}
                  loading={savingDraft}
                >
                  <FiSave className="mr-2" />
                  Save Draft
                </Button>
              )}

              <Button
                type="button"
                onClick={onSubmit}
                disabled={submitting || savingDraft || loading}
                loading={submitting}
                className="min-w-[150px]"
              >
                <FiCheck className="mr-2" />
                {mode === "edit" ? "Update Order" : "Create Order"}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Loading Overlay */}
      {loading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-sm w-full mx-4">
            <div className="flex flex-col items-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mb-4"></div>
              <p className="text-gray-900 font-medium">Loading...</p>
              <p className="text-gray-500 text-sm mt-1">Please wait</p>
            </div>
          </div>
        </div>
      )}

      {/* User Selection Modal */}
      {showUserSelection && (
        <UserSelection
          isOpen={showUserSelection}
          onClose={() => setShowUserSelection(false)}
          onUserSelected={handleSelectUser}
          mode="smart"
          title="Select Customer"
          allowSearch={true}
          allowCreate={true}
          allowEdit={false}
          autoFillFromSearch={true}
          showEmail={true}
          showKYC={false}
          showAddress={true}
          showGSTIN={true}
          showAlternatePhone={false}
          requiredFields={[]}
        />
      )}

      {/* Product Selection Modal */}
      {showProductSelection && (
        <ProductSelection
          isOpen={showProductSelection}
          onClose={() => setShowProductSelection(false)}
          onProductSelected={handleAddProducts}
          mode="smart"
          title="Select Products"
          multiSelect={true}
          allowSearch={true}
          allowCreate={false}
          allowEdit={false}
          autoFillFromSearch={true}
          showStockManagement={true}
          showPricing={true}
          showHallmark={true}
          showManufacturing={true}
          showDisplaySettings={false}
          filterByBranch={true}
          filterByType={null}
          filterByStatus="ACTIVE"
          requiredFields={["purity"]}
        />
      )}
    </div>
  );
};

// Section Wrapper Component
// Section Wrapper Component
const SectionWrapper = ({
  id,
  title,
  icon: Icon,
  completed,
  optional = false,
  children,
}) => {
  return (
    <div id={id} className="bg-white rounded-lg shadow">
      {children}
    </div>
  );
};

export default OrderForm;
